import React, { useState, useEffect } from 'react';
import { Play, Square, Timer, Map as MapIcon, Zap, ChevronRight, Clock, DollarSign, Target } from 'lucide-react';
import { TrackingService } from '../../services/TrackingService';
import storage from '../../services/storageService';
import { useApp } from '../../contexts/AppContext';
import { DEFAULT_FUEL_CONFIG, getFuelKmCost, normalizeFuelConfig } from '../../utils/fuelCost';

const TrackerWidget = ({ navigate, isDark, vehicles = [], selectedVehicleId = null, setConfirmModal, onStopTracker, onOpenEarningsTracker, platformBreakdown = null }) => {
    const { localTracker, dailyGoal } = useApp();
    const [trackingState, setTrackingState] = useState(TrackingService.getState());
    const [uiWorkTime, setUiWorkTime] = useState(0);
    const [fuelConfig, setFuelConfig] = useState(() => ({ ...DEFAULT_FUEL_CONFIG }));

    useEffect(() => {
        // Carregar configuração de combustível salva (se houver) ou do veículo
        const savedFuel = storage.getItemSync('tracker_fuel_config');
        if (savedFuel) {
            setFuelConfig(normalizeFuelConfig(savedFuel));
        } else if (vehicles && selectedVehicleId) {
            const vehicle = vehicles.find(v => String(v.id) === String(selectedVehicleId));
            if (vehicle && vehicle.consumption) {
                setFuelConfig(prev => ({
                    ...prev,
                    consumption: vehicle.consumption.toString().replace('.', ',')
                }));
            }
        }
    }, [selectedVehicleId, vehicles]);

    useEffect(() => {
        const handleUpdate = (newState) => {
            setTrackingState(newState);
        };

        TrackingService.addListener(handleUpdate);

        // Timer de UI
        let interval;
        if (trackingState.isTracking && trackingState.startTime) {
            interval = setInterval(() => {
                setUiWorkTime(Math.floor((Date.now() - trackingState.startTime) / 1000));
            }, 1000);
        } else {
            setUiWorkTime(0);
        }

        return () => {
            clearInterval(interval);
            TrackingService.removeListener(handleUpdate);
        };
    }, [trackingState.isTracking, trackingState.startTime]);

    const formatTime = (totalSeconds) => {
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const calculateCost = () => {
        const distance = trackingState.distances || 0;
        return distance * getFuelKmCost(fuelConfig);
    };

    if (!trackingState.isTracking) return null;

    // Calcular Gastos Estimados
    const estimatedCost = calculateCost();

    // Calcular Métricas em Tempo Real (usando dados do novo modal de ganhos progressivos)
    const currentIncome = localTracker?.totalAmount || 0;
    const currentTrips = localTracker?.totalTrips || 0;
    const currentDistance = trackingState.distances || 0;
    const currentHours = uiWorkTime / 3600; // Segundos para horas

    const safeDiv = (num, den) => (den > 0.016) ? (num / den) : 0; // Evita divisão por quase zero (1 minuto)

    const incomePerHour = safeDiv(currentIncome, currentHours);
    const incomePerKm = safeDiv(currentIncome, currentDistance);
    const incomePerTrip = safeDiv(currentIncome, currentTrips);

    // Previsão da meta diária usando o ritmo atual da Jornada Pro
    const targetGoal = Number(dailyGoal) || 0;
    const remainingToGoal = Math.max(0, targetGoal - currentIncome);
    const isGoalReached = targetGoal > 0 && currentIncome >= targetGoal;
    let goalEtaTime = null;
    if (!isGoalReached && targetGoal > 0 && incomePerHour > 0 && currentHours >= 0.025) {
        const goalHoursNeeded = remainingToGoal / incomePerHour;
        if (goalHoursNeeded <= 24) {
            goalEtaTime = new Date(Date.now() + (goalHoursNeeded * 3600000))
                .toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        }
    }

    // Formatador BRL rápido
    const fBRL = (v) => `R$ ${v.toFixed(2).replace('.', ',')}`;

    return (
        <div
            onClick={() => navigate('tracker')}
            className="bg-gradient-to-br from-indigo-600 to-indigo-800 dark:from-indigo-900 dark:to-gray-900 text-white p-5 rounded-3xl shadow-xl mb-4 animate-slide-up cursor-pointer overflow-hidden relative border border-indigo-400/20 group hover:scale-[1.01] transition-transform"
        >
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:bg-white/15 transition-colors"></div>

            <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex items-center gap-2 opacity-90">
                    <Zap size={14} className="text-yellow-400 animate-pulse" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Jornada Pro Ativa</span>
                </div>
                <div className="flex items-center gap-1.5">
                    {onOpenEarningsTracker && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenEarningsTracker();
                            }}
                            className="bg-emerald-500/20 hover:bg-emerald-500/30 px-2 py-1 rounded-lg flex items-center gap-1 border border-emerald-400/30 transition-colors active:scale-95"
                            title="Abrir Acompanhar Ganhos"
                        >
                            <DollarSign size={12} />
                            <span className="text-[10px] font-bold">Ganhos</span>
                        </button>
                    )}
                    <div className="bg-white/10 px-2 py-1 rounded-lg flex items-center gap-1">
                        <MapIcon size={12} />
                        <span className="text-[10px] font-bold">Ver Mapa</span>
                        <ChevronRight size={12} />
                    </div>
                </div>
            </div>

            <div className="mb-3 relative z-10">
                <span className="text-[9px] font-bold uppercase tracking-widest text-white/60">Acumulado</span>
                <div className="text-2xl sm:text-3xl font-black leading-tight tracking-tight">
                    {fBRL(currentIncome)}
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2 relative z-10">
                <div className="flex flex-col items-center bg-white/5 p-2 rounded-xl backdrop-blur-sm border border-white/5">
                    <div className="flex items-center gap-1 opacity-70 mb-1">
                        <Clock size={12} className="text-indigo-300" />
                        <span className="text-[9px] uppercase tracking-tighter">Tempo / Hr</span>
                    </div>
                    <span className="text-sm font-bold leading-tight">{formatTime(uiWorkTime)}</span>
                    <span className="text-xs font-black text-indigo-300 mt-0.5">{fBRL(incomePerHour)}</span>
                </div>

                <div className="flex flex-col items-center bg-white/5 p-2 rounded-xl backdrop-blur-sm border border-white/5">
                    <div className="flex items-center gap-1 opacity-70 mb-1">
                        <Timer size={12} className="text-blue-300" />
                        <span className="text-[9px] uppercase tracking-tighter">Distância / KM</span>
                    </div>
                    <span className="text-sm font-bold leading-tight">{currentDistance.toFixed(2)}<span className="text-[10px] ml-0.5 text-blue-200">km</span></span>
                    <span className="text-xs font-black text-blue-300 mt-0.5">{fBRL(incomePerKm)}</span>
                </div>

                <div className="flex flex-col items-center bg-white/5 p-2 rounded-xl backdrop-blur-sm border border-white/5">
                    <div className="flex items-center gap-1 opacity-70 mb-1">
                        <Zap size={12} className="text-emerald-300" />
                        <span className="text-[9px] uppercase tracking-tighter">Viagens / Média</span>
                    </div>
                    <span className="text-sm font-bold leading-tight text-white">{currentTrips} <span className="text-[10px] font-normal text-emerald-200 uppercase tracking-tighter"></span></span>
                    <span className="text-xs font-black text-emerald-300 mt-0.5">{fBRL(incomePerTrip)}</span>
                </div>
            </div>

            {platformBreakdown && (
                <div className="mt-3 px-3 py-2 rounded-xl bg-black/20 border border-white/10 relative z-10">
                    <div className="flex items-center justify-between mb-1.5 gap-2">
                        <span className="text-[8px] font-black uppercase tracking-wider text-indigo-200/80">
                            Origem dos Ganhos
                        </span>
                        <span className="text-[8px] font-bold text-white/50 text-right">
                            {platformBreakdown.map(p => `${p.name} ${p.percent}%`).join(' • ')}
                        </span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden flex mb-2">
                        {platformBreakdown.map(item => (
                            <div
                                key={item.id}
                                className={`h-full ${item.style.bar}`}
                                style={{ width: `${item.percent}%` }}
                                title={`${item.name}: ${item.percent}% (${fBRL(item.amount)})`}
                            />
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {platformBreakdown.map(item => (
                            <div
                                key={item.id}
                                className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-white/5 border border-white/10 text-[9px]"
                            >
                                <span className={`w-1.5 h-1.5 rounded-full ${item.style.dot}`}></span>
                                <span className="font-bold text-white/90">{item.name}</span>
                                <span className={`font-black ${item.style.text}`}>{item.percent}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {targetGoal > 0 && (
                <div className="mt-3 flex items-center gap-2.5 p-3 rounded-2xl bg-gradient-to-r from-indigo-500/25 via-indigo-500/10 to-emerald-500/20 border border-indigo-400/30 backdrop-blur-md relative z-10">
                    <div className={`p-2 rounded-xl shrink-0 border ${isGoalReached
                        ? 'bg-emerald-500/25 text-emerald-300 border-emerald-400/30'
                        : 'bg-indigo-500/25 text-indigo-200 border-indigo-400/30'
                        }`}>
                        <Target size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[8px] font-black uppercase tracking-[0.14em] text-indigo-200/90 block">
                            {isGoalReached ? 'Meta Diária' : 'Previsão da Meta'}
                        </span>
                        <div className="text-sm font-black text-white leading-tight">
                            {isGoalReached
                                ? <span className="text-emerald-300">Batida! 🎉</span>
                                : goalEtaTime
                                    ? <>às <span className="text-base text-emerald-300">{goalEtaTime}</span></>
                                    : <>Faltam {fBRL(remainingToGoal)}</>}
                        </div>
                    </div>
                    {!isGoalReached && goalEtaTime && (
                        <div className="text-right shrink-0">
                            <span className="text-[7px] font-bold uppercase text-white/50 block">Faltam</span>
                            <span className="text-[11px] font-black text-white">{fBRL(remainingToGoal)}</span>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-2 flex gap-2 relative z-10">
                <div className="flex-1 flex justify-between items-center bg-black/20 p-2 rounded-xl border border-white/5">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Custo Est. Combustível</span>
                    <span className="text-[11px] font-black text-red-300">{fBRL(estimatedCost)}</span>
                </div>
            </div>

            <div className="mt-3 flex gap-2 relative z-10">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        TrackingService.toggleWakeLock();
                        setTrackingState(TrackingService.getState());
                    }}
                    className={`flex-1 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 border ${trackingState.hasWakeLock
                        ? 'bg-amber-500/20 border-amber-500/50 text-amber-200'
                        : 'bg-white/5 border-white/10 text-gray-400'
                        }`}
                >
                    <Zap size={12} className={trackingState.hasWakeLock ? 'fill-current text-amber-400' : ''} />
                    {trackingState.hasWakeLock ? 'Tela Ativa: SIM' : 'Tela Ativa: NÃO'}
                </button>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setConfirmModal({
                            type: 'stopTracking',
                            title: 'Encerrar Rastreamento?',
                            message: 'Deseja realmente encerrar o rastreamento da Jornada Pro agora?',
                            confirmText: 'Sim, Encerrar',
                            onConfirm: async () => {
                                if (onStopTracker) {
                                    await onStopTracker();
                                } else {
                                    await TrackingService.finishRoute();
                                }
                                setTrackingState(TrackingService.getState());
                                setConfirmModal(null);
                            }
                        });
                    }}
                    className="flex-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 text-red-200"
                >
                    <Square size={12} fill="currentColor" /> Encerrar
                </button>
            </div>
        </div>
    );
};

export default TrackerWidget;
